import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
// Constants
import {
  ABOUT_PAGE_URL,
  ADMIN_CONTACT_FORM_PAGE_URL,
  CLIPS_FANSITES_PAGE_URL,
  CONTACT_PAGE_URL,
  GALLERY_PAGE_URL,
  HOME_PAGE_URL,
  LINKS_PAGE_URL,
  LOGIN_PAGE_URL,
  MAINTENANCE_PAGE_URL,
  POLICIES_PAGE_URL,
  QUESTIONNAIRE_PAGE_URL,
  SERVICES_PAGE_URL,
  TESTIMONIALS_PAGE_URL,
} from './utils/Routes';
import {
  COOKIE_TIMER,
  CookiePolicyName,
} from './utils/Constants';
// Components
import LoadingScreen from './components/utils/LoadingScreen';
// Utils
import HomePage from './pages/home/HomePage';
import ClipsPage from './pages/clips/ClipsPage';
import ServicesPage from './pages/services/ServicesPage';
import ContactPage from './pages/contact/ContactPage';
import LinksPage from './pages/links/LinksPage';
import AboutMePage from './pages/about/AboutMePage';
import GalleryPage from './pages/gallery/GalleryPage';
import LoginPage from './users/login/LoginPage';
import TestimonialsPage from './pages/testimonials/TestimonialsPage';
import ApplicationPage from './pages/application/ApplicationPage';
import ContactFormAdminPage from './pages/contact/admin/ContactFormAdminPage';// Lazy-loaded Pages
import { AuthenticateAdmin } from './utils/user/AuthenticateUser';

// Auth
const MaintenancePage = lazy(() =>
  import('./pages/maintenance/MaintenancePage')
);
const Error404 = lazy(() => import('./pages/error/Error404'));
const TermAndPoliciesPage = lazy(() =>
  import('./pages/policies/TermAndPoliciesPage')
);

// Components
const CookieConsentModal = lazy(() =>
  import('./components/modals/CookieConsentModal')
);

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAgreedToCookies, setHasAgreedToCookies] = useState(true);

  useEffect(() => {
    const cookie = localStorage.getItem(CookiePolicyName);

    if (cookie) {
      setHasAgreedToCookies(true);
    } else {
      setHasAgreedToCookies(false);
    }
  }, []);

  useEffect(() => {
    if (hasAgreedToCookies) {
      setIsVisible(false);
    }

    const timer = setTimeout(() => {
      if (!hasAgreedToCookies) {
        setIsVisible(true);
      }
    }, COOKIE_TIMER);

    return () => clearTimeout(timer);
  }, [hasAgreedToCookies]);

  return (
    <>
      {isVisible && (
        <Suspense>
          <CookieConsentModal setHasAgreedToCookies={setHasAgreedToCookies} />
        </Suspense>
      )}

      {/* Suspense component wraps the lazy-loaded components */}
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Main page routes */}
          <Route path={HOME_PAGE_URL} index element={<HomePage />} />

          {/* Eager loaded */}
          <Route path={CONTACT_PAGE_URL} element={<ContactPage />} />
          <Route path={GALLERY_PAGE_URL} element={<GalleryPage />} />
          <Route path={ABOUT_PAGE_URL} element={<AboutMePage />} />
          <Route path={SERVICES_PAGE_URL} element={<ServicesPage />} />
          <Route path={LINKS_PAGE_URL} element={<LinksPage />} />
          <Route path={QUESTIONNAIRE_PAGE_URL} element={<ApplicationPage />} />
          <Route path={CLIPS_FANSITES_PAGE_URL} element={<ClipsPage />} />
          <Route path={TESTIMONIALS_PAGE_URL} element={<TestimonialsPage />} />
          <Route path={POLICIES_PAGE_URL} element={<TermAndPoliciesPage />} />

          {/* Contact admin routes */}
          <Route
            path={ADMIN_CONTACT_FORM_PAGE_URL}
            element={
              <AuthenticateAdmin>
                <ContactFormAdminPage />
              </AuthenticateAdmin>
            }
          />

          {/* Other */}
          <Route path={MAINTENANCE_PAGE_URL} element={<MaintenancePage />} />
          <Route path={LOGIN_PAGE_URL} element={<LoginPage />} />

          {/* Error routes */}
          <Route path='*' element={<Error404 />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
